import { useState, useCallback, useRef, useEffect, type DragEvent, type ChangeEvent } from "react";
import clsx from "clsx";
import { format } from "date-fns";
import { Button } from "../components/atoms";
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "../components/atoms/Dialog/Dialog";
import {
    useUploadFileMutation,
    useGetPendingCalculationQuery,
    useLazyGetCalculationStatusQuery,
    useLazyDownloadCalculationResultQuery,
    useLazyDownloadUploadedFileQuery,
    useGetCalculationHistoryQuery,
    type FileUploadAcceptedResponse,
    type FileUploadApiError,
    type ValidationIssue,
} from "../shared/api/endpoints/fileUploadApi";


// ドキュメント+上矢印アイコン（デザイン準拠）
const FileUploadIcon = () => (
    <svg
        viewBox="0 0 60 72"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-14 h-[68px] text-brand-500"
        aria-hidden="true"
    >
        <path
            d="M0 8C0 3.58172 3.58172 0 8 0H38L60 22V64C60 68.4183 56.4183 72 52 72H8C3.58172 72 0 68.4183 0 64V8Z"
            fill="currentColor"
            fillOpacity="0.12"
            stroke="currentColor"
            strokeWidth="2.5"
        />
        <path
            d="M38 0L60 22H42C39.7909 22 38 20.2091 38 18V0Z"
            fill="currentColor"
            fillOpacity="0.28"
            stroke="currentColor"
            strokeWidth="2"
        />
        <path
            d="M30 52V34M30 34L22 42M30 34L38 42"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/** ポーリング間隔（ミリ秒） */
const POLLING_INTERVAL_MS = 3000;

/** 計算リクエストのステータス表示定義 */
const CALC_STATUS_MAP: Record<number, { label: string; className: string }> = {
    0: { label: "処理中", className: "text-brand-600" },
    1: { label: "成功", className: "text-success-600" },
    2: { label: "エラー", className: "text-error-500" },
} as const;

/** UTC ISO文字列を「YYYY/MM/DD HH:mm」（日本時間）に変換 */
const formatUploadedAt = (iso: string | null): string => {
    if (!iso) return "-";
    return format(new Date(iso), "yyyy/MM/dd HH:mm");
};

/** ファイル名の拡張子の前にタイムスタンプを付与する（例: schedule_20260707143052.xlsx）
 *  isoDate を指定した場合はその日時、未指定の場合は現在日時を使用する */
const appendTimestamp = (filename: string, isoDate?: string | null): string => {
    const d = isoDate ? new Date(isoDate) : new Date();
    const timestamp = format(d, "yyyyMMddHHmmss");
    const dotIndex = filename.lastIndexOf(".");
    if (dotIndex === -1) return `${filename}_${timestamp}`;
    return `${filename.slice(0, dotIndex)}_${timestamp}${filename.slice(dotIndex)}`;
};

/** 出力ファイル名の先頭に「【結果】」を付与する */
const addResultPrefix = (filename: string): string => `【結果】${filename}`;

const RaceSchedulePage = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<"idle" | "calculating" | "success" | "error">("idle");
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [validationIssues, setValidationIssues] = useState<ValidationIssue[] | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollingResolvedRef = useRef(false);
    const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
    const { data: pendingData, refetch: refetchPending } = useGetPendingCalculationQuery(undefined, {
        // ページ表示時に必ず最新を取得（キャッシュを使わない）
        refetchOnMountOrArgChange: true,
    });
    const [triggerGetStatus] = useLazyGetCalculationStatusQuery();
    const [triggerDownloadResult] = useLazyDownloadCalculationResultQuery();
    const { data: historyData, refetch: refetchHistory, isLoading: isHistoryLoading } = useGetCalculationHistoryQuery();
    const [triggerDownloadUploaded] = useLazyDownloadUploadedFileQuery();

    /** 履歴からファイルをダウンロード */
    const handleHistoryDownload = useCallback(async (id: number, filename: string, type: "uploaded" | "result", uploadedAt: string | null) => {
        try {
            const blob = type === "uploaded"
                ? await triggerDownloadUploaded(id).unwrap()
                : await triggerDownloadResult(id).unwrap();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const timestampedName = appendTimestamp(filename, uploadedAt);
            a.download = type === "result" ? addResultPrefix(timestampedName) : timestampedName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            // ダウンロード失敗は静かに無視（ネットワークエラー等）
        }
    }, [triggerDownloadUploaded, triggerDownloadResult]);

    /** ポーリングタイマーを停止 */
    const stopPolling = useCallback(() => {
        if (pollingTimerRef.current !== null) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    }, []);

    /** 計算結果Excelをダウンロード */
    const downloadResult = useCallback(async (calcRequestId: number, fileName: string, uploadedAt: string | null) => {
        try {
            const blob = await triggerDownloadResult(calcRequestId).unwrap();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = addResultPrefix(appendTimestamp(fileName, uploadedAt));
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setUploadStatus("success");
            void refetchHistory();
        } catch {
            setUploadStatus("error");
            setUploadError("結果ファイルのダウンロードに失敗しました。");
        }
    }, [triggerDownloadResult, refetchHistory]);

    /** ポーリング開始: 計算状態を定期的に確認 */
    const startPolling = useCallback((calcRequestId: number, fileName: string, uploadedAt: string | null) => {
        stopPolling();
        pollingResolvedRef.current = false;
        pollingTimerRef.current = setInterval(async () => {
            // 既に完了/エラー処理済みなら何もしない（複数リクエストの競合防止）
            if (pollingResolvedRef.current) return;
            try {
                const result = await triggerGetStatus(calcRequestId, false).unwrap();
                if (pollingResolvedRef.current) return;
                if (result.status === "completed") {
                    pollingResolvedRef.current = true;
                    stopPolling();
                    await downloadResult(calcRequestId, fileName, result.uploaded_at ?? uploadedAt);
                } else if (result.status === "error") {
                    pollingResolvedRef.current = true;
                    stopPolling();
                    setUploadStatus("error");
                    setUploadError(result.error_message ?? "計算中にエラーが発生しました。");
                }
                // pending の場合は次のポーリングまで待つ
            } catch {
                if (pollingResolvedRef.current) return;
                pollingResolvedRef.current = true;
                stopPolling();
                setUploadStatus("error");
                setUploadError("計算状態の確認に失敗しました。");
            }
        }, POLLING_INTERVAL_MS);
    }, [stopPolling, triggerGetStatus, downloadResult]);

    // ページ表示時: 処理中のリクエストがあれば「計算中」状態を復元
    useEffect(() => {
        if (!pendingData?.has_pending) return;

        setUploadStatus("calculating");
        if (pendingData.calculation_request_id !== null) {
            // DBレコードあり → 計算サーバーの結果をポーリング
            startPolling(pendingData.calculation_request_id, pendingData.uploaded_filename, pendingData.uploaded_at);
        }
    }, [pendingData, startPolling]);

    // 計算中かつDBレコード未作成（アドバイザリロック中）の場合、
    // calculation-pending を定期的に手動リフェッチしてレコード作成を検出する
    useEffect(() => {
        if (uploadStatus !== "calculating") return;
        // calculation_request_id が確定したら calculation-status のポーリングに任せる
        if (pendingData?.has_pending && pendingData.calculation_request_id !== null) return;

        const timer = setInterval(() => {
            void refetchPending();
        }, POLLING_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [uploadStatus, pendingData, refetchPending]);

    // アンマウント時にポーリングを停止
    useEffect(() => {
        return () => { stopPolling(); };
    }, [stopPolling]);

    const handleFileSelect = useCallback((file: File) => {
        setSelectedFile(file);
    }, []);

    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setIsDragOver(false);
            if (isUploading || uploadStatus === "calculating") return;
            const file = e.dataTransfer.files[0];
            if (file) handleFileSelect(file);
        },
        [handleFileSelect, isUploading, uploadStatus],
    );

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
        },
        [handleFileSelect],
    );

    const handleSelectClick = useCallback(() => {
        if (isUploading || uploadStatus === "calculating") return;
        fileInputRef.current?.click();
    }, [isUploading, uploadStatus]);

    // アップロード実行処理
    const handleExecute = useCallback(async () => {
        if (!selectedFile) return;
        setUploadStatus("idle");
        setUploadError(null);
        setValidationIssues(null);
        try {
            const result = await uploadFile(selectedFile).unwrap();
            const fileName = selectedFile.name;

            if (result instanceof Blob) {
                // 同期モード（relay_enabled=false）: 従来通りExcelを即ダウンロード
                const url = URL.createObjectURL(result);
                const a = document.createElement("a");
                a.href = url;
                a.download = addResultPrefix(appendTimestamp(fileName));
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                setUploadStatus("success");
                void refetchHistory();
            } else {
                // 非同期モード: 計算中の状態にしてポーリング開始
                const accepted = result as FileUploadAcceptedResponse;
                setUploadStatus("calculating");
                startPolling(accepted.calculation_request_id, fileName, accepted.uploaded_at);
            }

            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (err) {
            setUploadStatus("error");
            const apiError = (err as { data?: FileUploadApiError }).data;
            if (apiError?.details && apiError.details.length > 0) {
                setValidationIssues(apiError.details);
            } else {
                setUploadError(apiError?.message ?? "アップロードに失敗しました。ファイルを確認して再度お試しください。");
            }
        }
    }, [selectedFile, uploadFile, startPolling]);

    const handleCloseValidationDialog = useCallback(() => {
        setValidationIssues(null);
        setUploadStatus("idle");
    }, []);

    // アップロード中 or 計算中はUI操作を無効にする
    const isBusy = isUploading || uploadStatus === "calculating";

    // バリデーションエラーを種別ごとに分類
    const missingSheets = validationIssues?.filter((i) => i.type === "missing_sheet") ?? [];
    const missingColumnsIssues = validationIssues?.filter((i) => i.type === "missing_columns") ?? [];
    const totalColumnCount = missingColumnsIssues.reduce(
        (sum, i) => sum + (i.type === "missing_columns" ? i.columns.length : 0),
        0,
    );

    return (
        <div className="px-8 py-6">

        {/* ── バリデーションエラー Dialog ───── */}
        {validationIssues !== null && (
            <Dialog onClose={handleCloseValidationDialog} className="w-[600px]">
                <DialogHeader title="ファイルに問題があります" onClose={handleCloseValidationDialog} />
                <DialogBody className="flex flex-col gap-5 max-h-[520px] overflow-y-auto">
                    <p className="text-sm text-gray-600">
                        以下の問題を修正してから再度アップロードしてください。
                    </p>

                    {/* 不足シート */}
                    {missingSheets.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                                    シート
                                </span>
                                <p className="text-sm font-semibold text-gray-700">
                                    シートが見つかりません（{missingSheets.length}件）
                                </p>
                            </div>
                            <ul className="rounded-lg border border-orange-200 bg-orange-50 divide-y divide-orange-100">
                                {missingSheets.map((issue) => (
                                    <li key={issue.sheet} className="flex items-center gap-2 px-4 py-2.5 text-sm text-orange-800">
                                        <span className="font-bold leading-none shrink-0">✕</span>
                                        <span>「{issue.sheet}」シートがファイルに存在しません</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* 不足カラム */}
                    {missingColumnsIssues.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-error-100 text-error-700">
                                    カラム
                                </span>
                                <p className="text-sm font-semibold text-gray-700">
                                    カラムが見つかりません（{totalColumnCount}件）
                                </p>
                            </div>
                            <div className="rounded-lg border border-error-200 divide-y divide-error-100">
                                {missingColumnsIssues.map((issue) => {
                                    if (issue.type !== "missing_columns") return null;
                                    return (
                                        <div key={issue.sheet} className="px-4 py-3 bg-error-50">
                                            {/* シート名 */}
                                            <p className="text-sm font-semibold text-gray-800 mb-2">
                                                {issue.sheet}
                                            </p>
                                            {/* 不足カラム */}
                                            <ul className="flex flex-col gap-1 mb-3">
                                                {issue.columns.map((col) => (
                                                    <li key={col} className="flex items-center gap-2 text-sm text-error-700 pl-2">
                                                        <span className="font-bold leading-none shrink-0">✕</span>
                                                        <span>必須カラム「{col}」が見つかりません</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            {/* 実際のヘッダー */}
                                            <div className="bg-white rounded border border-error-200 px-3 py-2">
                                                <p className="text-xs text-gray-500 mb-1">
                                                    ファイル内の実際のヘッダー行
                                                </p>
                                                <p className="text-xs text-gray-700 break-all">
                                                    {issue.actual_headers.length > 0
                                                        ? issue.actual_headers.join("　/　")
                                                        : "（ヘッダー行が空です）"}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" size="md" onClick={handleCloseValidationDialog}>
                        閉じる
                    </Button>
                </DialogFooter>
            </Dialog>
        )}
        <div className="w-3/4 mx-auto flex flex-col gap-6">
            {/* ── ページタイトル ───── */}
            <div className="bg-white rounded-lg border border-gray-200 px-6 py-4">
                <h1 className="text-xl font-bold text-brand-500 border-l-4 border-brand-500 pl-3">
                    レース日程作成
                </h1>
            </div>

            {/* ── Excelファイルアップロードセクション ───── */}
            <section>
                <h2 className="text-base font-semibold text-brand-500 mb-3">
                    Excelファイルアップロード
                </h2>
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <p className="text-sm text-gray-700 mb-4">
                        テンプレートファイルの形で作成したExcelファイル、またはCSVファイルをアップロードしてください。
                    </p>

                    {/* ドラッグ＆ドロップゾーン */}
                    <div
                        onDragOver={isBusy ? undefined : handleDragOver}
                        onDragLeave={isBusy ? undefined : handleDragLeave}
                        onDrop={isBusy ? undefined : handleDrop}
                        className={clsx(
                            "border border-dashed rounded-lg py-10 px-6",
                            "flex flex-col items-center gap-3 transition-colors duration-200",
                            isBusy
                                ? "border-gray-300 bg-gray-50 cursor-not-allowed"
                                : isDragOver
                                  ? "border-brand-400 bg-brand-50"
                                  : "border-brand-300 bg-brand-50/30",
                        )}
                    >
                        <FileUploadIcon />
                        <div className="text-center leading-relaxed">
                            <p className="text-sm text-brand-500">
                                {isUploading
                                    ? "アップロード中です。しばらくお待ちください..."
                                    : uploadStatus === "calculating"
                                      ? "自動作成処理中です。しばらくお待ちください..."
                                      : "アップロードするファイルをドラッグ＆ドロップ"}
                            </p>
                            {!isBusy && (
                                <p className="text-sm text-brand-500">または</p>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="hidden"
                            disabled={isBusy}
                            onChange={handleInputChange}
                        />
                        {!isBusy && (
                            <Button variant="outline" size="md" onClick={handleSelectClick}>
                                {selectedFile ? selectedFile.name : "ファイルを選択"}
                            </Button>
                        )}
                    </div>

                    {/* ステータスメッセージ */}
                    {uploadStatus === "calculating" && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-brand-600">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <span>レース日程を自動作成中です。完了すると自動でダウンロードが開始されます。</span>
                        </div>
                    )}
                    {uploadStatus === "success" && (
                        <p className="mt-3 text-sm text-success-600">
                            処理が完了しました。結果ファイルがダウンロードされました。
                        </p>
                    )}
                    {uploadStatus === "error" && uploadError && (
                        <p className="mt-3 text-sm text-error-500">{uploadError}</p>
                    )}

                    {/* 実行ボタン */}
                    <div className="flex justify-end mt-5">
                        <Button
                            variant="primary"
                            size="md"
                            disabled={selectedFile === null || isBusy}
                            onClick={handleExecute}
                        >
                            {isUploading ? "アップロード中..." : uploadStatus === "calculating" ? "計算中..." : "日程自動作成を実行"}
                        </Button>
                    </div>
                </div>
            </section>

            {/* ── アップロード履歴セクション ──────────── */}
            <section>
                <h2 className="text-base font-semibold text-brand-500 mb-3">
                    アップロード履歴
                </h2>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="rounded-lg overflow-hidden border border-[#D9D9D9]">
                    <div className="max-h-[340px] overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0">
                            <tr className="bg-brand-50 border-b border-brand-200">
                                <th className="text-left px-5 py-3 font-medium text-gray-700 w-44">
                                    日時
                                </th>
                                <th className="text-left px-5 py-3 font-medium text-gray-700 w-40">
                                    ユーザー
                                </th>
                                <th className="text-left px-5 py-3 font-medium text-gray-700 w-40">
                                    アップロード結果
                                </th>
                                <th className="text-left px-5 py-3 font-medium text-gray-700 w-36">
                                    アップロードファイル
                                </th>
                                <th className="text-left px-5 py-3 font-medium text-gray-700 w-36">
                                    結果ファイル
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isHistoryLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">
                                        読み込み中...
                                    </td>
                                </tr>
                            ) : (!historyData || historyData.length === 0) ? (
                                <tr>
                                    <td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">
                                        履歴データはありません
                                    </td>
                                </tr>
                            ) : (
                                historyData.map((item) => {
                                    const statusDef = CALC_STATUS_MAP[item.status] ?? { label: "不明", className: "text-gray-500" };
                                    return (
                                        <tr key={item.id} className="border-b border-gray-100 last:border-b-0">
                                            <td className="px-5 py-3 text-gray-700">
                                                {formatUploadedAt(item.uploaded_at)}
                                            </td>
                                            <td className="px-5 py-3 text-gray-700">
                                                {item.account_name ?? "-"}
                                            </td>
                                            <td className={clsx("px-5 py-3 font-medium", statusDef.className)}>
                                                {statusDef.label}
                                            </td>
                                            <td className="px-5 py-3">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleHistoryDownload(item.id, item.uploaded_filename, "uploaded", item.uploaded_at)}
                                                >
                                                    ダウンロード
                                                </Button>
                                            </td>
                                            <td className="px-5 py-3">
                                                {item.has_result_file ? (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleHistoryDownload(item.id, item.uploaded_filename, "result", item.uploaded_at)}
                                                    >
                                                        ダウンロード
                                                    </Button>
                                                ) : (
                                                    <span className="text-sm text-gray-400">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                    </div>
                    </div>
                </div>
            </section>
        </div>
        </div>
    );
};

export default RaceSchedulePage;
